import { InvestorAccessPermission } from 'further-types/further-user';
import { UserType } from 'further-types/user';
import useUser from 'hooks/ui/useUser';

export type AuthClaims = {
  furtherUserId: string;
  email: string;
  activeUserType: UserType | undefined;
  isAdminUserImpersonatingFurtherUser?: boolean;
  isReadOnlyUser: boolean;
  hasDocumentSigningPermissions: boolean;
  setEmailOverride: (email: string) => void;
};

// When a user changes their email, there can be a short period where the
// client-side user/session state still reflects the previous email.
// To avoid UI inconsistencies, we allow an email override stored in localStorage.
export function useAuthClaims(): AuthClaims {
  const user = useUser();
  const emailOverride = localStorage.getItem('furtherUserEmailOverride');
  const setEmailOverride = (email: string) => {
    localStorage.setItem('furtherUserEmailOverride', email);
  };

  const investorAccessPermissions = Array.isArray(
    user.investorAccessPermissions,
  )
    ? user.investorAccessPermissions
    : [];

  const isReadOnlyUser =
    user.userType === UserType.Investor &&
    investorAccessPermissions.length === 1 &&
    investorAccessPermissions[0] === InvestorAccessPermission.ReadAll;

  const hasDocumentSigningPermissions = investorAccessPermissions.includes(
    InvestorAccessPermission.SignDocuments,
  );

  return {
    furtherUserId: user.furtherUserId ?? '',
    email: emailOverride ?? user.email ?? '',
    activeUserType: user.userType,
    isAdminUserImpersonatingFurtherUser:
      user.isAdminUserImpersonatingFurtherUser,
    isReadOnlyUser,
    hasDocumentSigningPermissions,
    setEmailOverride,
  };
}
